'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import * as Sentry from '@sentry/nextjs';
import { useAuth } from '@/hooks/use-auth';
import { storePostAuthRedirect, clearPostAuthRedirect } from '@/lib/auth/post-auth-redirect';
import { emailSchema, loginPasswordSchema } from '@/lib/form-validators';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/auth/turnstile-widget';
import {
  AuthInput,
  AuthButton,
  AuthLayout,
  AuthDivider,
  OAuthButtons,
} from '@/components/auth/auth-layout';
import { BrandedLoadingScreen } from '@/components/auth/branded-loading-screen';

// Allowlist of characters valid in a relative URL path (RFC 3986 path + pct-encoded).
// Rejects protocol-relative URLs (//), absolute URLs, and path traversal attempts.
const SAFE_PATH_RE = /^\/(?:[a-zA-Z0-9_\-.~!$&'()*+,;=:@/]|%[0-9A-Fa-f]{2})*$/;

function isSafeRedirect(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  if (!url.startsWith('/') || url.startsWith('//')) return false;
  return SAFE_PATH_RE.test(url);
}

const loginSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
});

type FieldErrors = Record<string, string>;

export function LoginForm() {
  const { signInWithPassword } = useAuth();
  const searchParams = useSearchParams();
  const rawRedirect = searchParams.get('redirect');
  const redirectTo = rawRedirect && isSafeRedirect(rawRedirect) ? rawRedirect : null;

  // Invitation flow: pre-fill the email when arriving from invite link.
  // URL param is SSR-safe; sessionStorage fallback deferred to useEffect
  // to avoid hydration mismatch (server sees null, client may not).
  const isInviteFlow = searchParams.get('invite') === 'true';
  const urlInviteEmail = isInviteFlow ? searchParams.get('email') : null;
  const [isInviteLocked, setIsInviteLocked] = useState(!!urlInviteEmail);

  // Display actionable error messages from auth callback redirects.
  // These surface when exchangeCodeForSession or email verification fails
  // (e.g. expired link, cross-device PKCE, backend sync failure).
  const callbackError = searchParams.get('error');
  const callbackErrorShown = useRef(false);
  useEffect(() => {
    if (!callbackError || callbackErrorShown.current) return;
    callbackErrorShown.current = true;
    const messages: Record<string, string> = {
      callback_failed:
        'Your sign-in link has expired or was opened in a different browser. Please sign in again.',
      setup_failed:
        'Account setup could not be completed. Please try signing in — your account may already be ready.',
      verification_failed:
        'Email verification link expired. Please sign in and verify with the 6-digit code instead.',
    };
    toast.error(messages[callbackError] ?? 'Something went wrong. Please try again.');
  }, [callbackError]);

  const [email, setEmail] = useState(urlInviteEmail ?? '');

  useEffect(() => {
    if (!isInviteFlow || urlInviteEmail) return;
    try {
      const stored = sessionStorage.getItem('virza-invite-email');
      if (stored) {
        setEmail(stored);
        setIsInviteLocked(true);
      }
    } catch {
      // Storage unavailable
    }
  }, [isInviteFlow, urlInviteEmail]);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileResetRef = useRef<(() => void) | null>(null);

  // OAuth redirect state — disables the entire form when provider redirect is in progress
  const [oauthRedirecting, setOauthRedirecting] = useState(false);

  // Client-side rate limiting — prevents rapid brute-force attempts
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockCountdown, setLockCountdown] = useState(0);

  // Combine both: lockedUntil is set synchronously on lockout,
  // lockCountdown catches up on the next interval tick (~200ms).
  const isLocked = lockedUntil !== null || lockCountdown > 0;

  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, lockedUntil - Date.now());
      setLockCountdown(Math.ceil(remaining / 1000));
      if (remaining <= 0) {
        setLockedUntil(null);
        setLockCountdown(0);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked || oauthRedirecting) return;
    setFieldErrors({});
    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const errors: FieldErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!errors[key]) errors[key] = issue.message;
      });
      setFieldErrors(errors);
      return;
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }

    setLoading(true);
    try {
      // Store the redirect target BEFORE sign-in so signInWithPassword
      // can consume it for workspace-aware navigation. This avoids the
      // double-navigation race where a hard nav to bare /home
      // overwrites the correct /w/{wsId}/home soft nav.
      if (redirectTo) storePostAuthRedirect(redirectTo);
      await signInWithPassword(email, password, turnstileToken ?? undefined);
      // signInWithPassword handles sessionStorage flag + router.push
      // to the correct workspace-scoped URL. We just show the branded
      // loading screen and let the hook drive navigation.
      setFailedAttempts(0);
      setAuthSuccess(true);
      return;
    } catch (error) {
      // Clear any stored redirect so a failed attempt doesn't leak
      // into a future auth flow (e.g. user switches to OAuth/signup).
      clearPostAuthRedirect();
      Sentry.captureException(error, { level: 'warning', tags: { flow: 'sign_in' } });
      const next = failedAttempts + 1;
      setFailedAttempts(next);
      if (next >= 5) {
        setLockedUntil(Date.now() + 30_000);
        setFailedAttempts(0);
        toast.error('Too many failed attempts. Please wait 30 seconds.');
      } else {
        toast.error('Invalid email or password. Please try again.');
      }
      turnstileResetRef.current?.();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Branded success transition — full-screen overlay (not inside AuthLayout)
  // to avoid the duplicate logo caused by AuthLayout's own VirzaMark header.
  if (authSuccess) {
    return <BrandedLoadingScreen message="Signing you in…" className="fixed inset-0 z-50" />;
  }

  return (
    <AuthLayout>
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">Sign in</h1>
        <p className="text-[15px] text-white/40">Enter your email and password to continue.</p>
      </div>

      <div className="mb-6 space-y-4">
        <OAuthButtons
          onOAuthStart={() => setOauthRedirecting(true)}
          onOAuthReset={() => setOauthRedirecting(false)}
        />
        <AuthDivider />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            if (isInviteLocked) return;
            setEmail(e.target.value);
            setFieldErrors((prev: FieldErrors) => {
              const next = { ...prev };
              delete next.email;
              return next;
            });
          }}
          error={fieldErrors.email}
          required
          readOnly={isInviteLocked}
          autoComplete="email"
          autoFocus={!isInviteLocked}
          aria-invalid={!!fieldErrors.email}
          className={isInviteLocked ? 'cursor-not-allowed opacity-60' : ''}
        />
        {isInviteLocked && (
          <p className="-mt-2 text-xs text-white/30">
            This email matches your invitation and can&apos;t be changed.
          </p>
        )}

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-[13px] font-medium text-white/50">Password</label>
            <Link
              href="/forgot-password"
              className="text-[13px] text-white/30 transition-colors hover:text-white/60"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev: FieldErrors) => {
                  const next = { ...prev };
                  delete next.password;
                  return next;
                });
              }}
              required
              autoComplete="current-password"
              aria-invalid={!!fieldErrors.password}
              className={`h-11 w-full rounded-lg border bg-white/[0.05] px-3.5 pr-11 text-[15px] text-white transition-all duration-150 placeholder:text-white/25 focus:border-white/20 focus:bg-white/[0.07] focus:ring-2 focus:ring-white/[0.08] focus:outline-none ${fieldErrors.password ? 'border-red-500/50 focus:ring-red-500/20' : 'border-white/[0.08]'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-white/25 transition-colors hover:text-white/50"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {fieldErrors.password && (
            <p className="mt-1.5 text-[13px] text-red-400" role="alert">
              {fieldErrors.password}
            </p>
          )}
        </div>

        <TurnstileWidget
          onVerify={setTurnstileToken}
          onError={() => setTurnstileToken(null)}
          onExpire={() => setTurnstileToken(null)}
          resetRef={turnstileResetRef}
        />

        {isLocked && (
          <p className="text-center text-sm text-red-400" role="alert">
            Too many failed attempts. Try again in {lockCountdown}s.
          </p>
        )}

        <div className="pt-2">
          <AuthButton
            type="submit"
            loading={loading}
            disabled={oauthRedirecting || isLocked || (TURNSTILE_ENABLED && !turnstileToken)}
          >
            {loading ? 'Signing in…' : isLocked ? `Locked (${lockCountdown}s)` : 'Sign in'}
          </AuthButton>
        </div>
      </form>

      <p className="mt-8 text-center text-sm text-white/30">
        Don&apos;t have an account?{' '}
        <Link href="/signup" className="font-medium text-white/60 hover:text-white">
          Create account
        </Link>
      </p>
    </AuthLayout>
  );
}
